import { computed, inject, Injectable, InjectionToken, Provider, Signal, signal } from '@angular/core';
import { Assignment } from '../../api/app-types/assignment';
import { Client, RestrictedClient } from '../../api/app-types/client.app';
import { CLIENT_API } from '../../api/injection-tokens/services.token';
import { ASSIGNMENT_API } from '../../api/services/assignment.service';
import { mockClient } from '../../mock-data/client.mock';
import { REPRESENTATION_MODE } from './representation-mode.service';

export interface StateProvider {
	setCurrentClient(clientId: number | string): Promise<void>;
	updateAssignments(): Promise<void>;
	updateClient(): Promise<void>;
	currentClient: Signal<Client | RestrictedClient | undefined>;
	currentAssignments: Signal<Assignment[]>;
	currentContacts: Signal<Record<number, Assignment['contacts']>>;
	hasAssignmentInTransition: Signal<Assignment | undefined>;
	assignmentIdInTransition: Signal<number | undefined>;
	previousAssignmentBeforeCurrent: Signal<Assignment | undefined>;
}

export const STATE = new InjectionToken<StateProvider>('STATE');

/**
 * Why does this service has "providedIn"?
 * It's because it's used by the desk component and sub components.
 * We want to make sure that only one instance exists (singleton) for the entire desk and its children. With this we don't have to reload all the data on navigation.
 */
@Injectable({
	providedIn: 'root',
})
export class State implements StateProvider {
	private currentLoadId = 0;
	private readonly clientApi = inject(CLIENT_API);
	private readonly assignmentApi = inject(ASSIGNMENT_API);
	private readonly representationMode = inject(REPRESENTATION_MODE, { optional: true });
	// Tracks the client id + representation context that is currently loaded successfully,
	// so that repeated calls for the same client (e.g. from Desk's effect and ContactView's
	// ngOnInit both resolving to the same id) don't re-fetch client/assignments/contacts.
	private loadedClientId: number | undefined;
	private loadedRepresentationKey: string | undefined;
	// Tracks loads that are currently in progress, keyed by "clientId:representationKey", so that
	// concurrent calls for the same client + context (e.g. both firing before either has resolved,
	// as happens on a cold/hard-reload navigation) join the same request instead of starting a
	// second one.
	private readonly inFlightLoads = new Map<string, Promise<void>>();
	private readonly _currentClient = signal<Client | RestrictedClient | undefined>(undefined);
	private readonly _currentClientId = computed(() => this._currentClient()?.id);
	private readonly _currentAssignments = signal<Assignment[]>([]);
	private readonly _currentContacts = signal<Record<number, Assignment['contacts']>>({});
	private readonly _hasAssignmentInTransition = computed(() =>
		this.flattenAssignments(this._currentAssignments()).find(
			(assignment) =>
				assignment.phaseChangeId != null &&
				assignment.phaseChangeId > 0 &&
				assignment.phaseChangeId !== assignment.phaseId,
		),
	);
	public readonly assignmentIdInTransition = computed(() => {
		const assignment = this.flattenAssignments(this._currentAssignments()).find(
			(assignment) =>
				assignment.phaseChangeId != null &&
				assignment.phaseChangeId > 0 &&
				assignment.phaseChangeId !== assignment.phaseId,
		);
		return assignment?.id;
	});

	public async setCurrentClient(clientId: number | string): Promise<void> {
		const id = Number(clientId);
		const representationKey = this.getRepresentationKey();

		// Already loaded this exact client under the same representation context: reuse it.
		if (
			id === this.loadedClientId &&
			representationKey === this.loadedRepresentationKey &&
			this._currentClient()?.id === id
		) {
			return;
		}

		// An identical load is already in progress: join it instead of starting a new one.
		const key = this.buildLoadKey(id, representationKey);
		const existingLoad = this.inFlightLoads.get(key);
		if (existingLoad) {
			return existingLoad;
		}

		const loadPromise = this.loadClient(id, representationKey);
		this.inFlightLoads.set(key, loadPromise);

		try {
			await loadPromise;
		} finally {
			// Only clear the entry if it's still the one we set, in case a later load for the
			// same key has already replaced it by the time this one settles.
			if (this.inFlightLoads.get(key) === loadPromise) {
				this.inFlightLoads.delete(key);
			}
		}
	}

	private async loadClient(id: number, representationKey: string): Promise<void> {
		const loadId = ++this.currentLoadId;

		const [client, assignments] = await Promise.all([
			this.clientApi.getSingleWithRestricted(id),
			this.assignmentApi.getAll(id),
		]);

		const assignmentIds = this.contactAssignmentIds(assignments);

		const contacts = assignmentIds.length ? await this.assignmentApi.getContactsBatch(id, assignmentIds) : {};

		if (loadId !== this.currentLoadId) {
			return;
		}

		this._currentClient.set(client);
		this._currentAssignments.set(assignments);
		this._currentContacts.set(contacts);
		this.loadedClientId = id;
		this.loadedRepresentationKey = representationKey;
	}

	private buildLoadKey(id: number, representationKey: string): string {
		return `${id}:${representationKey}`;
	}

	public async updateAssignments(): Promise<void> {
		const clientId = this._currentClientId();

		if (!clientId) {
			return;
		}

		const assignments = await this.assignmentApi.getAll(Number(clientId));

		const assignmentIds = this.contactAssignmentIds(assignments);

		const contacts = assignmentIds.length
			? await this.assignmentApi.getContactsBatch(Number(clientId), assignmentIds)
			: {};

		if (this._currentClientId() !== clientId) {
			return;
		}

		this._currentAssignments.set(assignments);
		this._currentContacts.set(contacts);
	}

	public async updateClient(): Promise<void> {
		const clientId = this._currentClientId();

		if (!clientId) {
			return;
		}

		const client = await this.clientApi.getSingleWithRestricted(Number(clientId));

		if (this._currentClientId() !== clientId) {
			return;
		}

		this._currentClient.set(client);
	}

	public get currentClient() {
		return this._currentClient.asReadonly();
	}

	public get currentClientId() {
		return this._currentClientId;
	}

	public get currentAssignments() {
		return this._currentAssignments.asReadonly();
	}

	public get currentContacts() {
		return this._currentContacts.asReadonly();
	}

	public get hasAssignmentInTransition() {
		return this._hasAssignmentInTransition;
	}

	public get previousAssignmentBeforeCurrent() {
		// gets the assignment which was the latest before the current active one
		return computed(() => {
			const assignments = this.flattenAssignments(this._currentAssignments())
				.filter((a) => a.phaseChangeId != null && a.phaseChangeId > 0 && a.phaseChangeId !== a.phaseId)
				.filter((a) => a.cancellationDate !== undefined)
				.sort((a, b) => {
					if (a.cancellationDate && b.cancellationDate) {
						return new Date(b.cancellationDate).getTime() - new Date(a.cancellationDate).getTime();
					}
					return 0;
				});
			return assignments.length > 1 ? assignments[1] : undefined;
		});
	}

	private flattenAssignments(assignments: Assignment[]): Assignment[] {
		return assignments.flatMap((assignment) => [assignment, ...(assignment.additionalAssignments ?? [])]);
	}

	private contactAssignmentIds(assignments: Assignment[]): number[] {
		// Child access is independent of parent access; filter after flattening.
		return this.flattenAssignments(assignments)
			.filter((assignment) => !assignment.isRestricted)
			.map((assignment) => assignment.id);
	}

	/**
	 * Identifies the "acting as" context a client/assignments load was fetched under, so that
	 * toggling representation mode correctly triggers a refetch instead of reusing data that
	 * may differ (e.g. via the X-Act-As header) for the previous context.
	 */
	private getRepresentationKey(): string {
		const mode = this.representationMode?.mode();
		return mode?.active ? `rep:${mode.representedUserId ?? ''}` : 'self';
	}
}

@Injectable({
	providedIn: 'root',
})
export class StateMock implements StateProvider {
	public async setCurrentClient(_clientId: number | string): Promise<void> {
		return Promise.resolve();
	}
	public async updateAssignments(): Promise<void> {
		// mock implementation
		return Promise.resolve();
	}
	public async updateClient(): Promise<void> {
		// mock implementation
		return Promise.resolve();
	}
	public currentClient: Signal<Client | RestrictedClient | undefined> = signal<Client>(mockClient);
	public currentAssignments: Signal<Assignment[]> = signal<Assignment[]>([]);
	public currentContacts: Signal<Record<number, Assignment['contacts']>> = signal<
		Record<number, Assignment['contacts']>
	>({});
	public hasAssignmentInTransition: Signal<Assignment | undefined> = signal<Assignment | undefined>(undefined);
	public assignmentIdInTransition: Signal<number | undefined> = signal<number | undefined>(undefined);
	public previousAssignmentBeforeCurrent: Signal<Assignment | undefined> = signal<Assignment | undefined>(undefined);
}

export const provideState: Provider = {
	provide: STATE,
	useClass: State,
};
